import { useNavigate } from '@tanstack/react-router';
import { Command, useCommandState } from 'cmdk';

import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { useManagementUsersPagination } from '../../../user/list/hooks/useManagementUsers';
import { useDebounce } from '../../hooks/useDebounce';
import { useRole } from '../../hooks/useRole';
import { useAutoSelectFirstCommandItem } from '../hooks/useAutoSelectFirstCommandItem';
import { useShortcutMenu } from '../hooks/useShortcutMenu';

import { CommandEmptyState } from './CommandEmptyState';
import { CommandErrorState } from './CommandErrorState';
import { CommandItem } from './CommandItem';
import { CommandSkeletonList } from './CommandSkeletonList';

export const UserSearch = () => {
    const search = useCommandState((state) => state.search);
    const debouncedSearch = useDebounce(search, 300);
    const { setOpen } = useShortcutMenu();
    const role = useRole();

    const query = useManagementUsersPagination({ searchQuery: debouncedSearch, page: 0 });

    useAutoSelectFirstCommandItem(query.data?.content[0]?.id);

    const navigate = useNavigate();

    const onUserSelect = (user: UserGatewayMessage) => {
        if (role.isSuperAdmin()) {
            navigate({ to: '/members/$id', params: { id: user.id } });
            setOpen(false);
            return;
        }

        if (role.isCustomer()) {
            navigate({
                to: '/customer-groups/$groupId/members/$userId',
                params: { groupId: user.groupId, userId: user.id },
            });
            setOpen(false);
            return;
        }
    };

    if (query.isPending) {
        return <CommandSkeletonList />;
    }

    if (query.isError) {
        return <CommandErrorState />;
    }

    if (!query.data || query.data.content.length === 0) {
        return <CommandEmptyState />;
    }

    const formatUser = (user: UserGatewayMessage) => {
        if (!user.firstname || !user.lastname) {
            return user.email;
        }

        return `${user.firstname} ${user.lastname} (${user.email})`;
    };

    return (
        <Command.List>
            {query.data.content.map((user: UserGatewayMessage) => (
                <CommandItem key={user.id} value={user.id} onSelect={() => onUserSelect(user)}>
                    {formatUser(user)}
                </CommandItem>
            ))}
        </Command.List>
    );
};
